import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { saFetch, SALoading, fmtDateTime, inputCls, btnPrimary } from "./saCommon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Trash, Megaphone, CircleNotch } from "@phosphor-icons/react";

const EMPTY = {
  title: "", content: "", type: "info", audience: "all", tenant_ids: [], plan_id: "",
  show_as: "banner", publish_at: "", expires_at: "", link_url: "", link_label: "", send_email: false,
};

const TYPE_BADGE = {
  info: "border-sky-500/40 text-sky-500",
  warning: "border-amber-500/40 text-amber-500",
  promo: "border-primary/50 text-primary",
  maintenance: "border-red-500/40 text-red-400",
};
const TYPE_LABELS = { info: "Información", warning: "Aviso", promo: "Promoción", maintenance: "Mantenimiento" };
const AUDIENCE_LABELS = { all: "Todas", active: "Solo activas", inactive: "Solo suspendidas", expiring: "Por vencer (7d)", overdue: "Vencidas", plan: "Por plan", selected: "Seleccionadas" };

export default function SAMensajes() {
  const [items, setItems] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    saFetch("/superadmin/announcements").then(setItems).catch((e) => toast.error(e.message));
  }, []);
  useEffect(load, [load]);
  useEffect(() => {
    saFetch("/superadmin/companies-v2?per_page=200").then((d) => setCompanies(d.items || [])).catch(() => {});
    saFetch("/superadmin/plans").then(setPlans).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await saFetch("/superadmin/announcements", { method: "POST", body: JSON.stringify(form) });
      toast.success("Anuncio publicado");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await saFetch(`/superadmin/announcements/${toDelete.id}`, { method: "DELETE" });
      toast.success("Anuncio eliminado");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const toggleTenant = (id) =>
    setForm((p) => ({ ...p, tenant_ids: p.tenant_ids.includes(id) ? p.tenant_ids.filter((x) => x !== id) : [...p.tenant_ids, id] }));

  if (!items) return <SALoading label="Cargando anuncios..." />;

  return (
    <div data-testid="sa-mensajes">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground">Mensajes y anuncios</h1>
          <p className="text-sm text-muted-foreground mt-1">Comunicados que ven las empresas en su portal</p>
        </div>
        <Button className={btnPrimary} data-testid="sa-new-announcement" onClick={() => { setForm(EMPTY); setOpen(true); }}>
          <Plus className="mr-2" size={15} /> Nuevo anuncio
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="border border-border rounded-sm p-10 text-center text-muted-foreground bg-card" data-testid="sa-announcements-empty">
          <Megaphone size={28} className="mx-auto mb-3" />
          Aún no has publicado anuncios.
        </div>
      ) : (
        <div className="space-y-3" data-testid="sa-announcements-list">
          {items.map((a) => (
            <div key={a.id} className="border border-border bg-card rounded-sm p-4 hover:border-primary/50 transition-colors duration-200" data-testid={`sa-ann-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-heading text-base font-bold text-foreground">{a.title}</p>
                    <Badge variant="outline" className={`rounded-sm font-normal ${TYPE_BADGE[a.type] || TYPE_BADGE.info}`}>{TYPE_LABELS[a.type] || a.type}</Badge>
                    <Badge variant="outline" className="rounded-sm border-border text-muted-foreground font-normal">{a.show_as === "modal" ? "Ventana emergente" : "Banner"}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{a.content}</p>
                  <p className="text-[11px] text-muted-foreground mt-2">
                    Audiencia: {AUDIENCE_LABELS[a.audience] || a.audience}
                    {a.audience !== "all" && a.sent_count >= 0 ? ` (${a.sent_count} empresas)` : ""}
                    {" · "}Leído por {(a.reads || []).length} usuario(s)
                    {" · "}{fmtDateTime(a.created_at)}
                  </p>
                </div>
                <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-950/40 shrink-0" data-testid={`sa-ann-delete-${a.id}`} onClick={() => setToDelete(a)}>
                  <Trash size={15} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Nuevo anuncio</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="sa-ann-form">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Título *</Label>
              <Input className={inputCls} required data-testid="sa-ann-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Contenido *</Label>
              <Textarea className={`${inputCls} min-h-[90px]`} required data-testid="sa-ann-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className={inputCls} data-testid="sa-ann-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="info">Información</SelectItem>
                  <SelectItem value="warning">Aviso importante</SelectItem>
                  <SelectItem value="promo">Promoción</SelectItem>
                  <SelectItem value="maintenance">Mantenimiento</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Mostrar como</Label>
              <Select value={form.show_as} onValueChange={(v) => setForm({ ...form, show_as: v })}>
                <SelectTrigger className={inputCls} data-testid="sa-ann-showas"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="banner">Banner superior</SelectItem>
                  <SelectItem value="modal">Ventana emergente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Audiencia</Label>
              <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                <SelectTrigger className={inputCls} data-testid="sa-ann-audience"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {Object.entries(AUDIENCE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.audience === "plan" && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Plan</Label>
                <Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })}>
                  <SelectTrigger className={inputCls} data-testid="sa-ann-plan"><SelectValue placeholder="Selecciona el plan" /></SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.audience === "selected" && (
              <div className="sm:col-span-2 space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Empresas ({form.tenant_ids.length})</Label>
                <div className="border border-border rounded-sm max-h-40 overflow-y-auto divide-y divide-border">
                  {companies.map((c) => (
                    <label key={c.tenant_id} className="flex items-center gap-2 p-2.5 text-sm text-foreground/80 cursor-pointer hover:bg-muted/50">
                      <Checkbox checked={form.tenant_ids.includes(c.tenant_id)} onCheckedChange={() => toggleTenant(c.tenant_id)} className="border-input" data-testid={`sa-ann-tenant-${c.tenant_id}`} />
                      {c.name || c.tenant_id.slice(0, 8)}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Publicar desde</Label>
              <Input type="datetime-local" className={inputCls} data-testid="sa-ann-publish" value={form.publish_at} onChange={(e) => setForm({ ...form, publish_at: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Expira</Label>
              <Input type="datetime-local" className={inputCls} data-testid="sa-ann-expires" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Enlace (opcional)</Label>
              <Input type="url" className={inputCls} placeholder="https://..." data-testid="sa-ann-link" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Texto del enlace</Label>
              <Input className={inputCls} placeholder="Ej. Más información" value={form.link_label} onChange={(e) => setForm({ ...form, link_label: e.target.value })} />
            </div>
            <DialogFooter className="sm:col-span-2">
              <Button type="submit" disabled={saving} className={btnPrimary} data-testid="sa-ann-save">
                {saving && <CircleNotch className="animate-spin mr-2" size={15} />} Publicar anuncio
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={() => setToDelete(null)}>
        <AlertDialogContent className="bg-card border-border rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-foreground">Eliminar anuncio</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Se eliminará "{toDelete?.title}" y dejará de mostrarse en los portales.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent" data-testid="sa-ann-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="rounded-sm bg-red-600 text-white hover:bg-red-500" data-testid="sa-ann-delete-confirm">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
